import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { useAuth } from '../hooks/useAuth.js';
import { ROUTES, NAV_GROUPS, hasMinRole, isRouteFeatureEnabled } from '../routes.js';
import { useSearch } from '../context/SearchContext.jsx';
import { YELLOW, BLACK } from '../lib/brand.js';

const BYPASS = import.meta.env.VITE_BYPASS_AUTH === 'true';
const NAV_GROUP_LS = (id) => `sitruna_hub_nav_${id}`;

// Inline SVG icon set — 1.7px stroke, round caps, 24x24 viewBox.
const ICONS = {
  clients: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19c.7-3 3-4.7 5.5-4.7s4.8 1.7 5.5 4.7" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M14.6 14.6c2.7 0 4.5 1.7 4.9 4.4" />
    </>
  ),
  catalogue: (
    <>
      <rect x="3.5" y="4" width="17" height="5" rx="1" />
      <rect x="3.5" y="10.5" width="17" height="3.5" rx="1" />
      <rect x="3.5" y="15.5" width="17" height="4" rx="1" />
    </>
  ),
  stock: (
    <>
      <path d="M4 7l8-3.5L20 7v10l-8 3.5L4 17z" />
      <path d="M4 7l8 3.5L20 7" />
      <path d="M12 10.5V20" />
    </>
  ),
  forecast: (
    <>
      <path d="M4 19h16" />
      <path d="M4 15l4-4 4 3 8-7" />
      <path d="M14 7h6v6" />
    </>
  ),
  copy: (
    <>
      <path d="M5 7v12a1 1 0 001 1h10" />
      <rect x="8" y="4" width="11" height="13" rx="1.4" />
    </>
  ),
  profit: (
    <>
      <path d="M4 19h16" />
      <path d="M6 16V9" />
      <path d="M11 16V5" />
      <path d="M16 16v-4" />
    </>
  ),
  reports: (
    <>
      <path d="M5 4h11l3 3v13a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
      <path d="M8 11h8M8 15h8M8 7h4" />
    </>
  ),
  alerts: (
    <>
      <path d="M6 16V11a6 6 0 0112 0v5l1.5 2H4.5z" />
      <path d="M10 20a2 2 0 004 0" />
    </>
  ),
  news: (
    <>
      <path d="M4 5a1 1 0 011-1h12l3 3v12a1 1 0 01-1 1H5a1 1 0 01-1-1z" />
      <path d="M8 9h8M8 12h8M8 15h5" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 19h16" />
      <path d="M4 14l4-5 4 3 3-4 5 6" />
      <path d="M4 7h1M4 11h1" />
    </>
  ),
  ppc: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
    </>
  ),
  growth: (
    <>
      <path d="M3 17l5-5 4 3 5-6 4 4" />
      <path d="M14 8h6v6" />
    </>
  ),
  keywords: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M20 20l-3.5-3.5" />
      <path d="M8 10.5h5M10.5 8v5" />
    </>
  ),
  marketshare: (
    <>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  intelligence: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M5.64 5.64l1.77 1.77M16.59 16.59l1.77 1.77M5.64 18.36l1.77-1.77M16.59 7.41l1.77-1.77" />
      <circle cx="12" cy="12" r="2.2" />
    </>
  ),
  marketsizing: (
    <>
      <path d="M3 17l4-8 4 4 4-6 4 2" />
      <path d="M3 21h18" />
    </>
  ),
  dealtracker: (
    <>
      <path d="M12 2l2.5 5.5H20l-4.5 3.5 1.5 6L12 14l-5 3 1.5-6L4 7.5h5.5z" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M20 20l-4-4" />
    </>
  ),
  chevron: (
    <path d="M9 6l6 6-6 6" />
  ),
};

function Icon({ name, size = 16 }) {
  const d = ICONS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d}
    </svg>
  );
}

// Full Sitruna wordmark — used when sidebar is expanded.
function SitrunaWordmark() {
  return (
    <svg
      width="96"
      height="22"
      viewBox="0 0 140 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Sitruna"
      role="img"
    >
      <path
        d="M21.1187 5.67407H23.328C25.1122 5.67407 26.5623 4.20256 26.5623 2.39207V0.219478C26.5623 0.100062 26.4674 0 26.3459 0L24.1366 0C22.3525 0 20.9023 1.47142 20.9023 3.28191V5.4545C20.9023 5.57777 21.001 5.67407 21.1187 5.67407Z"
        fill={YELLOW}
      />
      <path
        d="M88.9163 30.8627V9.72616C88.9163 9.60675 88.8214 9.50659 88.6999 9.50659H84.6457C84.528 9.50659 84.4293 9.60289 84.4293 9.72616V20.5198C84.4293 24.6377 81.5974 27.1454 78.6289 27.1454C75.6603 27.1454 73.1283 24.8457 73.1283 20.7894V9.72616C73.1283 9.60675 73.0334 9.50659 72.912 9.50659H68.816C68.6983 9.50659 68.5996 9.60289 68.5996 9.72616V20.8357C68.5996 25.5083 70.7975 31.3982 77.2471 31.3982C81.0052 31.3982 83.4499 28.902 84.2357 27.9621C84.3496 27.8273 84.5698 27.8812 84.6077 28.0584L84.9911 29.7995C85.1544 30.5469 85.8111 31.0823 86.5665 31.0823H88.6961C88.8138 31.0823 88.9125 30.986 88.9125 30.8627H88.9163Z"
        fill="currentColor"
      />
      <path
        d="M93.6875 9.68394V30.8205C93.6875 30.9399 93.7824 31.0401 93.9039 31.0401H97.9581C98.0758 31.0401 98.1745 30.9438 98.1745 30.8205V20.0269C98.1745 15.909 101.006 13.4012 103.975 13.4012C106.943 13.4012 109.475 15.7009 109.475 19.7572V30.8243C109.475 30.9438 109.57 31.0439 109.692 31.0439H113.788C113.905 31.0439 114.004 30.9476 114.004 30.8243V19.7148C114.004 15.0422 111.806 9.15234 105.357 9.15234C101.599 9.15234 99.1539 11.6485 98.3681 12.5884C98.2542 12.7233 98.034 12.6693 97.9961 12.4921L97.6127 10.751C97.4494 10.0037 96.7927 9.46822 96.0373 9.46822H93.9077C93.79 9.46822 93.6913 9.56452 93.6913 9.68779L93.6875 9.68394Z"
        fill="currentColor"
      />
      <path
        d="M21.4434 9.63778V29.3991C21.4434 30.3044 22.1646 31.0363 23.0567 31.0363H25.7975C25.9152 31.0363 26.0139 30.94 26.0139 30.8167V11.0554C26.0139 10.1501 25.2926 9.41821 24.4005 9.41821H21.6597C21.5421 9.41821 21.4434 9.51452 21.4434 9.63778Z"
        fill="currentColor"
      />
      <path
        d="M59.3777 13.6239C61.01 13.6239 62.035 14.0284 62.9726 14.7102C63.0789 14.7872 63.2307 14.7564 63.2915 14.637L65.2009 10.9005C65.2464 10.808 65.2275 10.6963 65.1515 10.6308C63.914 9.58303 61.9818 8.92432 60.0914 8.92432C57.9921 8.92432 55.8283 9.28642 54.2188 11.9097L53.9417 12.3643C53.843 12.526 53.6 12.4914 53.5507 12.3065L53.1179 10.7155C52.9243 10.0068 52.2866 9.51369 51.5615 9.51369H49.0523C48.9346 9.51369 48.8359 9.60999 48.8359 9.73326V30.8236C48.8359 30.943 48.9308 31.0432 49.0523 31.0432H53.3001C53.4178 31.0432 53.5165 30.9469 53.5165 30.8236V19.6679C53.5165 14.063 58.0035 13.6277 59.3777 13.6277V13.6239Z"
        fill="currentColor"
      />
      <path
        d="M120.602 28.7825C122.447 30.7009 125.051 31.714 128.145 31.714C128.179 31.714 128.213 31.714 128.247 31.714C130.681 31.6677 133.725 30.3773 134.818 27.9967L135.111 27.3611C135.202 27.1685 135.483 27.207 135.517 27.4189L135.889 29.6685C136.018 30.4582 136.694 31.036 137.479 31.036H139.784C139.901 31.036 140 30.9397 140 30.8164V9.7261C140 9.60668 139.905 9.50653 139.784 9.50653H137.423C136.625 9.50653 135.946 10.0998 135.828 10.901L135.536 12.8771C135.505 13.0851 135.236 13.1314 135.133 12.9503L134.826 12.3879C133.71 10.3386 131.117 8.96338 128.373 8.96338C128.338 8.96338 128.308 8.96338 128.274 8.96338C125.169 8.96338 122.439 10.0343 120.568 11.9796C118.662 13.9634 117.656 16.8217 117.656 20.2462C117.656 23.6708 118.674 26.7756 120.594 28.7787L120.602 28.7825ZM128.855 13.1237C132.024 13.1237 135.433 15.3617 135.433 20.2693C135.433 22.1338 134.849 23.8711 133.793 25.1692C132.594 26.6407 130.886 27.415 128.855 27.415C124.93 27.415 122.189 24.4681 122.189 20.2462C122.189 16.0243 124.93 13.1198 128.855 13.1198V13.1237Z"
        fill="currentColor"
      />
      <path
        d="M42.3264 26.8025C42.2922 26.6792 42.1594 26.6137 42.0417 26.6599C41.3204 26.9411 40.5498 27.1029 39.8855 27.1029C37.8812 27.1029 36.6854 25.774 36.6854 23.5513V13.7515C36.6854 13.6321 36.7803 13.5319 36.9018 13.5319H42.5162C42.6339 13.5319 42.7326 13.4356 42.7326 13.3124V9.76841C42.7326 9.649 42.6377 9.54884 42.5162 9.54884H36.9473C36.8297 9.54884 36.731 9.45254 36.731 9.32927V3.5511C36.731 3.40857 36.5981 3.30456 36.4614 3.33923L33.6941 4.0981C32.7564 4.35619 32.1035 5.21906 32.1035 6.2052V23.5513C32.1035 26.1746 32.7906 28.1854 34.1496 29.5259C35.4175 30.7778 37.2738 31.3942 39.5211 31.3094C40.8573 31.2709 41.9582 31.0783 43.1615 30.6507C43.2678 30.6122 43.3286 30.4966 43.2982 30.3849L42.3264 26.8063V26.8025Z"
        fill="currentColor"
      />
      <path
        d="M0.0457113 28.0236C-0.0188223 28.1084 -0.0150262 28.2278 0.0570996 28.3048C0.675863 28.9635 3.62543 31.8064 8.71599 31.8064C13.8065 31.8064 16.9497 28.1738 16.9497 24.8957C16.9497 21.6175 14.5506 18.6591 9.15633 18.1198C3.76209 17.5805 4.19484 12.9503 8.67423 12.6729C11.4606 12.4996 12.9448 13.7977 13.4155 14.3024C13.5067 14.3987 13.6585 14.391 13.742 14.2831L15.7539 11.6367C15.8261 11.5443 15.8109 11.4094 15.7198 11.3324C15.1048 10.8278 12.7209 9.09816 8.59451 8.87474C3.22684 8.58198 0.524019 11.9988 0.524019 15.4542C0.524019 18.9095 2.73334 21.3479 8.46165 21.9912C13.9812 22.6114 13.3396 27.6153 8.79191 27.7809C5.9866 27.8849 3.52293 25.9204 2.53215 25.0921C2.43725 25.0151 2.30059 25.0305 2.22846 25.1307L0.0457113 28.0236Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Sitruna lemon mark — used in the collapsed sidebar and as /favicon.svg.
// Keep visually identical to apps/web/public/favicon.svg.
function SitrunaLemon() {
  return (
    <svg width="28" height="28" viewBox="0 0 256 256" fill="none" aria-label="Sitruna" role="img">
      <path
        d="M28 138c0-60.7 49.3-110 110-110h82c4.4 0 8 3.6 8 8v82c0 60.7-49.3 110-110 110H36c-4.4 0-8-3.6-8-8v-82z"
        fill={YELLOW}
      />
      <path
        d="M101.4 174.7c-.6.8-.6 1.9.1 2.6 5.7 6 22.7 18.5 47.6 18.5 25 0 40.4-17.8 40.4-33.9 0-16-11.7-30.5-38.1-33.2-26.4-2.6-24.3-25.3-2.4-26.7 13.6-.8 20.9 5.5 23.2 8 .4.5 1.2.4 1.6-.1l9.8-12.9c.4-.4.3-1.1-.2-1.5-3-2.5-14.7-11-34.9-12.1-26.3-1.4-39.5 15.3-39.5 32.2 0 16.9 10.8 28.8 38.8 31.9 27 3 23.9 27.5 1.7 28.4-13.7.5-25.8-9.1-30.7-13.2-.4-.4-1.1-.4-1.5.1l-15.9 11.9z"
        fill={BLACK}
      />
    </svg>
  );
}

function SearchTrigger({ collapsed }) {
  const { open } = useSearch();
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Search (⌘K)"
      className={[
        'mx-2 my-1.5 flex items-center gap-2 rounded px-2.5 py-[7px]',
        'text-[#6E6E66] hover:text-[#A8A89E] hover:bg-white/5',
        'border border-white/[0.07] transition-colors',
        collapsed ? 'justify-center' : '',
      ].join(' ')}
    >
      <Icon name="search" size={14} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left text-[12.5px]">Search anything…</span>
          <kbd className="text-[10px] text-[#4A4A42] font-sans">⌘K</kbd>
        </>
      )}
    </button>
  );
}

function NavItem({ to, label, icon, collapsed }) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 px-3 py-1.5 mx-1 rounded text-[13px] font-medium transition-colors',
          isActive
            ? 'bg-sitruna-yellow/10 text-white'
            : 'text-[#C7C6BF] hover:bg-white/5 hover:text-white',
          collapsed ? 'justify-center' : '',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-sitruna-yellow"
            />
          )}
          <span className="shrink-0 inline-grid place-items-center">
            <Icon name={icon} size={16} />
          </span>
          {!collapsed && <span className="whitespace-nowrap">{label}</span>}
        </>
      )}
    </NavLink>
  );
}

function SectionLabel({ children, collapsed, isGroupCollapsed, onToggle }) {
  if (collapsed) {
    return <div className="my-2 mx-3 h-px bg-white/8" aria-hidden="true" />;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 pt-3.5 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#5E5E56] hover:text-[#8C8B82] transition-colors"
    >
      <span>{children}</span>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={['transition-transform duration-150', isGroupCollapsed ? '-rotate-90' : ''].join(' ')}
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  );
}

function CollapseToggle({ collapsed, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className={[
        'h-8 mx-2 my-2 flex items-center gap-2 rounded text-[12px] font-medium',
        'text-[#4A4A42] hover:text-[#8C8B82] hover:bg-white/5 transition-colors',
        collapsed ? 'justify-center px-0' : 'justify-start px-2.5',
      ].join(' ')}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={collapsed ? '' : 'rotate-180'}
      >
        <path d="M9 6l6 6-6 6" />
      </svg>
      {!collapsed && <span>Collapse</span>}
    </button>
  );
}

function loadGroupCollapsed() {
  const state = {};
  NAV_GROUPS.forEach((g) => {
    try {
      const stored = localStorage.getItem(NAV_GROUP_LS(g.id));
      state[g.id] = stored !== null ? stored === 'true' : (g.defaultCollapsed ?? false);
    } catch {
      state[g.id] = g.defaultCollapsed ?? false;
    }
  });
  return state;
}

export default function Sidebar({ collapsed, onToggle }) {
  const { role, email } = useAuth();
  const initials = (email || 'U')[0].toUpperCase();
  const [groupCollapsed, setGroupCollapsed] = useState(loadGroupCollapsed);

  function toggleGroup(id) {
    setGroupCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(NAV_GROUP_LS(id), String(next[id])); } catch { /* ignore */ }
      return next;
    });
  }

  return (
    <aside
      className={[
        'hidden md:flex flex-col shrink-0 border-r border-white/8 transition-[width] duration-200',
        collapsed ? 'w-14' : 'w-[232px]',
      ].join(' ')}
      style={{ backgroundColor: 'var(--sitruna-black)', color: 'var(--sitruna-cream)' }}
      aria-label="Primary"
    >
      {/* Logo */}
      <NavLink
        to="/clients"
        aria-label="Sitruna Hub home"
        className={[
          'h-12 flex items-center border-b border-white/8 shrink-0',
          collapsed ? 'justify-center px-0' : 'px-4',
        ].join(' ')}
      >
        {collapsed ? <SitrunaLemon /> : <SitrunaWordmark />}
      </NavLink>

      {/* Search trigger */}
      <SearchTrigger collapsed={collapsed} />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto pb-2 flex flex-col gap-px">
        {NAV_GROUPS.map((group) => {
          const navItems = ROUTES.filter(
            (r) =>
              r.group === group.id &&
              r.navVisible !== false &&
              hasMinRole(role, r.minRole) &&
              isRouteFeatureEnabled(r)
          );
          if (!navItems.length) return null;
          const isGroupCollapsed = !collapsed && !!groupCollapsed[group.id];
          return (
            <div key={group.id} role="group" aria-label={group.label} className="contents">
              <SectionLabel
                collapsed={collapsed}
                isGroupCollapsed={isGroupCollapsed}
                onToggle={() => toggleGroup(group.id)}
              >
                {group.label}
              </SectionLabel>
              {!isGroupCollapsed && navItems.map((r) => (
                <NavItem key={r.path} to={r.path} label={r.label} icon={r.icon} collapsed={collapsed} />
              ))}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-white/8">
        <CollapseToggle collapsed={collapsed} onToggle={onToggle} />
      </div>

      {/* Footer — user */}
      <div className="border-t border-white/8 p-2.5 flex items-center gap-2.5 text-[12px] min-h-[52px]"
        style={{ color: 'var(--sitruna-cream)' }}
      >
        {BYPASS ? (
          <>
            <div
              className="w-7 h-7 rounded-full bg-sitruna-yellow grid place-items-center text-sitruna-black text-xs font-bold shrink-0"
              title={email || 'Demo user'}
            >
              {initials}
            </div>
            {!collapsed && (
              <div className="min-w-0 leading-tight">
                <div className="text-white text-[12.5px] font-medium truncate">{email}</div>
                <div className="text-[11px] text-[#8C8B82] capitalize">
                  {role.replace('_', ' ')} · Sitruna
                </div>
              </div>
            )}
          </>
        ) : (
          <UserButton
            afterSignOutUrl="/"
            showName={!collapsed}
            appearance={{
              elements: {
                userButtonOuterIdentifier: 'text-[#B9B8B0] text-xs',
                userButtonBox: collapsed ? 'flex-row-reverse' : '',
              },
            }}
          />
        )}
      </div>
    </aside>
  );
}
